/**
 * Emotion API Client - Platform-agnostic
 * Uses fetch (available in both browser and React Native)
 */

import type { EmotionalState, Diagnosis, Baseline } from './types';

// Range-compress landmark snapshots: N snapshots × M points → 1 object with min/max/avg per point
function compressLandmarkRanges(snapshots: Array<{timestamp: number; landmarks: any[]}>): Record<string, {min: number; max: number; avg: number}> {
  if (!snapshots || snapshots.length === 0) return {};
  const acc: Record<string, {vals: number[]}> = {};
  for (const snap of snapshots) {
    if (!snap.landmarks) continue;
    for (let i = 0; i < snap.landmarks.length; i++) {
      const lm = snap.landmarks[i];
      const idx = lm.i ?? i; // support both {i, x, y, z} and positional arrays
      for (const axis of ['x', 'y', 'z'] as const) {
        if (lm[axis] == null) continue;
        const key = `${idx}_${axis}`;
        if (!acc[key]) acc[key] = { vals: [] };
        acc[key].vals.push(lm[axis]);
      }
    }
  }
  const result: Record<string, {min: number; max: number; avg: number}> = {};
  for (const [key, {vals}] of Object.entries(acc)) {
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const avg = vals.reduce((s, v) => s + v, 0) / vals.length;
    result[key] = { min: +min.toFixed(4), max: +max.toFixed(4), avg: +avg.toFixed(4) };
  }
  return result;
}

// Compute semantic body gestures from raw pose snapshots (frame-by-frame analysis)
interface BodyGestures {
  // Final boolean flags (may be post-processed by hysteresis)
  handNearFace: boolean;
  handNearMouth: boolean;
  handNearChin: boolean;
  headDown: boolean;
  leanForward: boolean;
  bodyStill: boolean;
  gesturesSummary: string;
  faceDetectionValid?: boolean; // indicates if face landmarks were available when analyzed
  // Raw detection ratios (frames matching / totalFrames) — useful for smoothing/hysteresis
  handFaceRatio?: number;
  handMouthRatio?: number;
  handChinRatio?: number;
  headDownRatio?: number;
  leanForwardRatio?: number;
  totalFrames?: number;
}

function computeBodyGestures(
  snapshots: Array<{timestamp: number; keypoints: any[]}>,
  isFaceDetectionValid: boolean = true,
  faceLandmarkSnapshots?: Array<{timestamp: number; landmarks: any[]}>
): BodyGestures | null {
  if (!snapshots || snapshots.length === 0) return null;

  let handFaceFrames = 0;
  let handMouthFrames = 0;
  let handChinFrames = 0;
  let headDownFrames = 0;
  let leanForwardFrames = 0;
  let totalFrames = 0;

  const handYValues: number[] = [];

  // If face landmarks are available, compute an average eye-distance (scale unit)
  // and average nose position to improve mouth/chin estimation.
  let faceScaleUnitAvg = 0;
  let avgNose: { x: number; y: number; z?: number } | null = null;
  if (faceLandmarkSnapshots && faceLandmarkSnapshots.length > 0) {
    // Prefer nose->chin distance (face length) as a robust scale unit.
    // Fallback to inter-eye distance only if nose/chin aren't available.
    let accFaceLen = 0;
    let accEye = 0;
    let accNoseX = 0;
    let accNoseY = 0;
    let accNoseZ = 0;
    let countFaceLen = 0;
    let countEye = 0;
    for (const fs of faceLandmarkSnapshots) {
      const lms = fs.landmarks || [];
      const nose = lms.find((lm: any) => (lm.i ?? -1) === 1);
      const chin = lms.find((lm: any) => (lm.i ?? -1) === 152);
      const leftEye = lms.find((lm: any) => (lm.i ?? -1) === 33);
      const rightEye = lms.find((lm: any) => (lm.i ?? -1) === 263);
      if (nose && chin && nose.x != null && chin.x != null) {
        const d = Math.sqrt((chin.x - nose.x) ** 2 + (chin.y - nose.y) ** 2);
        accFaceLen += d;
        countFaceLen++;
      }
      if (leftEye && rightEye && leftEye.x != null && rightEye.x != null) {
        const d = Math.sqrt((rightEye.x - leftEye.x) ** 2 + (rightEye.y - leftEye.y) ** 2);
        accEye += d;
        countEye++;
      }
      if (nose && nose.x != null) {
        accNoseX += nose.x;
        accNoseY += nose.y;
        accNoseZ += nose.z ?? 0;
      }
    }
    if (countFaceLen > 0) {
      // Small empirical multiplier to align face-length scale with shoulder->nose heuristics
      faceScaleUnitAvg = (accFaceLen / countFaceLen) * 2.5;
    } else if (countEye > 0) {
      faceScaleUnitAvg = accEye / countEye;
    }
    if (faceLandmarkSnapshots.length > 0 && (accNoseX || accNoseY)) {
      avgNose = {
        x: accNoseX / faceLandmarkSnapshots.length,
        y: accNoseY / faceLandmarkSnapshots.length,
        z: accNoseZ / faceLandmarkSnapshots.length,
      };
    }
  }

  for (const snap of snapshots) {
    if (!Array.isArray(snap.keypoints) || snap.keypoints.length < 17) continue;
    totalFrames++;

    // Prefer pose nose if available, otherwise fall back to averaged face nose
    const poseNose = snap.keypoints[0];
    const nose = poseNose && poseNose.x != null ? poseNose : (avgNose ? { x: avgNose.x, y: avgNose.y, z: avgNose.z } : undefined);
    const lShoulder = snap.keypoints[11];
    const rShoulder = snap.keypoints[12];
    const mouth = snap.keypoints[9];
    const mouthR = snap.keypoints[10];

    if (!nose || nose.visibility < 0.5) continue;

    // Use face-based scale if available, otherwise shoulder-to-nose distance as fallback
    let scaleUnit = 0.3; // fallback
    if (faceScaleUnitAvg && faceScaleUnitAvg > 0) {
      scaleUnit = faceScaleUnitAvg;
    } else if (lShoulder && rShoulder && lShoulder.visibility > 0.3 && rShoulder.visibility > 0.3 && nose && nose.y != null) {
      const shoulderMidY = (lShoulder.y + rShoulder.y) / 2;
      scaleUnit = Math.abs(shoulderMidY - nose.y) || 0.3;
    }

    // Mouth center — kp9/10 are mouthLeft/mouthRight in Pose Landmarker
    const hasMouth = mouth && mouthR && mouth.visibility > 0.3 && mouthR.visibility > 0.3;
    const mouthY = hasMouth ? (mouth.y + mouthR.y) / 2 : (nose && nose.y != null ? nose.y + scaleUnit * 0.25 : scaleUnit * 0.25);
    const mouthX = hasMouth ? (mouth.x + mouthR.x) / 2 : (nose && nose.x != null ? nose.x : 0.5);
    // Chin estimated below mouth (roughly 40% of scaleUnit below nose)
    const chinY = nose.y + scaleUnit * 0.4;
    const chinX = nose.x;

    // Prefer hand landmarks (from Hand Landmarker) if present on the snapshot
    let frameHandFace = false, frameHandMouth = false, frameHandChin = false;
    let closestFaceDist = Infinity;
    if ((snap as any).handLandmarks && Array.isArray((snap as any).handLandmarks) && (snap as any).handLandmarks.length > 0) {
      for (const hk of (snap as any).handLandmarks) {
        if (hk.x == null || hk.y == null) continue;
        handYValues.push(hk.y);
        const distToNose = Math.sqrt((hk.x - nose.x) ** 2 + (hk.y - nose.y) ** 2);
        const distToMouth = Math.sqrt((hk.x - mouthX) ** 2 + (hk.y - mouthY) ** 2);
        const distToChin = Math.sqrt((hk.x - chinX) ** 2 + (hk.y - chinY) ** 2);
        if (distToNose < closestFaceDist) closestFaceDist = distToNose;
        if (distToNose < scaleUnit * 1.0) frameHandFace = true;
        if (distToMouth < scaleUnit * 0.7) frameHandMouth = true;
        if (distToChin < scaleUnit * 0.7) frameHandChin = true;
      }
    } else {
      // Fallback to pose-based hand keypoints (if present)
      for (let i = 15; i <= 22; i++) {
        const kp = snap.keypoints[i];
        if (!kp || kp.visibility < 0.5) continue;
        handYValues.push(kp.y);
        const distToNose = Math.sqrt((kp.x - nose.x) ** 2 + (kp.y - nose.y) ** 2);
        const distToMouth = Math.sqrt((kp.x - mouthX) ** 2 + (kp.y - mouthY) ** 2);
        const distToChin = Math.sqrt((kp.x - chinX) ** 2 + (kp.y - chinY) ** 2);
        if (distToNose < closestFaceDist) closestFaceDist = distToNose;
        if (distToNose < scaleUnit * 1.0) frameHandFace = true;
        if (distToMouth < scaleUnit * 0.7) frameHandMouth = true;
        if (distToChin < scaleUnit * 0.7) frameHandChin = true;
      }
    }
    // Log first frame per chunk for debugging
    if (totalFrames <= 2) {
      console.log(`🔍 bodyGestures frame ${totalFrames}: scaleUnit=${scaleUnit.toFixed(3)} closestHandToFace=${closestFaceDist.toFixed(3)} threshold=${(scaleUnit*1.5).toFixed(3)} handFace=${frameHandFace}`);
    }
    if (frameHandFace) handFaceFrames++;
    if (frameHandMouth) handMouthFrames++;
    if (frameHandChin) handChinFrames++;

    if (nose.y > 0.5) headDownFrames++;
    if (nose.z < -0.6) leanForwardFrames++;
  }

  if (totalFrames === 0) return null;

  // Diagnostic log — remove after debugging
  console.log(`🦴 bodyGestures: ${totalFrames} frames | handFace=${handFaceFrames}/${totalFrames} handMouth=${handMouthFrames}/${totalFrames} handChin=${handChinFrames}/${totalFrames} headDown=${headDownFrames}/${totalFrames} leanFwd=${leanForwardFrames}/${totalFrames} [faceValid=${isFaceDetectionValid}]`);

  // Produce raw ratios and boolean decisions at a conservative baseline.
  const handFaceRatio = totalFrames > 0 ? handFaceFrames / totalFrames : 0;
  const handMouthRatio = totalFrames > 0 ? handMouthFrames / totalFrames : 0;
  const handChinRatio = totalFrames > 0 ? handChinFrames / totalFrames : 0;
  const headDownRatio = totalFrames > 0 ? headDownFrames / totalFrames : 0;
  const leanForwardRatio = totalFrames > 0 ? leanForwardFrames / totalFrames : 0;

  // Conservative baseline booleans (pre-hysteresis): keep thresholds low here; final smoothing is applied at caller
  const BASE_THRESHOLD = 0.25;
  const handNearFace = isFaceDetectionValid && handFaceRatio >= BASE_THRESHOLD;
  const handNearMouth = isFaceDetectionValid && handMouthRatio >= BASE_THRESHOLD;
  const handNearChin = isFaceDetectionValid && handChinRatio >= BASE_THRESHOLD;
  const headDown = headDownRatio >= BASE_THRESHOLD;
  const leanForward = leanForwardRatio >= BASE_THRESHOLD;

  let bodyStill = true;
  if (handYValues.length >= 2) {
    const avg = handYValues.reduce((s, v) => s + v, 0) / handYValues.length;
    const variance = handYValues.reduce((s, v) => s + (v - avg) ** 2, 0) / handYValues.length;
    bodyStill = variance < 0.005;
  }

  const parts: string[] = [];
  if (handNearMouth) parts.push('hand near mouth');
  else if (handNearChin) parts.push('hand near chin');
  else if (handNearFace) parts.push('hand near face');
  if (headDown) parts.push('head down');
  if (leanForward) parts.push('leaning forward');
  if (bodyStill) parts.push('body still');
  else parts.push('body moving');

  return {
    handNearFace,
    handNearMouth,
    handNearChin,
    headDown,
    leanForward,
    bodyStill,
    gesturesSummary: parts.join(', ') || 'no notable gestures',
    faceDetectionValid: isFaceDetectionValid,
    handFaceRatio: +handFaceRatio.toFixed(3),
    handMouthRatio: +handMouthRatio.toFixed(3),
    handChinRatio: +handChinRatio.toFixed(3),
    headDownRatio: +headDownRatio.toFixed(3),
    leanForwardRatio: +leanForwardRatio.toFixed(3),
    totalFrames,
  };
}

// Range-compress body pose snapshots: N snapshots × 25 keypoints → ranges per keypoint
function compressBodyPoseRanges(snapshots: Array<{timestamp: number; keypoints: any[]}>): Record<string, {min: number; max: number; avg: number}> {
  if (!snapshots || snapshots.length === 0) return {};
  const acc: Record<string, {vals: number[]}> = {};
  for (const snap of snapshots) {
    if (!Array.isArray(snap.keypoints)) continue;
    snap.keypoints.forEach((kp: any, i: number) => {
      for (const axis of ['x', 'y', 'z'] as const) {
        if (kp[axis] == null) continue;
        const key = `${i}_${axis}`;
        if (!acc[key]) acc[key] = { vals: [] };
        acc[key].vals.push(kp[axis]);
      }
      if (kp.visibility != null) {
        const key = `${i}_vis`;
        if (!acc[key]) acc[key] = { vals: [] };
        acc[key].vals.push(kp.visibility);
      }
    });
  }
  const result: Record<string, {min: number; max: number; avg: number}> = {};
  for (const [key, {vals}] of Object.entries(acc)) {
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const avg = vals.reduce((s, v) => s + v, 0) / vals.length;
    result[key] = { min: +min.toFixed(4), max: +max.toFixed(4), avg: +avg.toFixed(4) };
  }
  return result;
}

export interface MilestoneData {
  sessionId: string;
  states: EmotionalState[];
  baseline?: Baseline;
  audioBase64?: string | null;
  bodyPoseSnapshots?: Array<{timestamp: number; keypoints: any; worldLandmarks?: any}>;
  heartRate?: { bpm: number; confidence: number; signalQuality: string };
  chunkIndex?: number;
  isMilestone?: boolean;
  windowSeconds?: number;
  snapshotCount?: number;
  isFaceDetectionValid?: boolean; // NEW: whether face detection was valid during this chunk
  signal?: AbortSignal;
}

export class EmotionAPIClient {
  private apiUrl: string;
  private authToken: string;
  // Keep previous body gestures to apply hysteresis across calls
  private prevBodyGestures?: BodyGestures;

  constructor(apiUrl: string, authToken: string) {
    this.apiUrl = apiUrl;
    this.authToken = authToken;
  }

  // Apply simple hysteresis using previous ratios to reduce flapping
  private applyHysteresis(raw: BodyGestures): BodyGestures {
    const prev = this.prevBodyGestures;
    const ON_THRESH = 0.5; // require 50% of frames to turn a flag on
    const OFF_THRESH = 0.25; // drop below 25% to turn it off

    const decide = (prevVal: boolean | undefined, ratio: number | undefined, faceValid = true) => {
      const r = ratio ?? 0;
      if (!faceValid) return false;
      if (prevVal) return r >= OFF_THRESH;
      return r >= ON_THRESH;
    };

    const handNearFace = decide(prev?.handNearFace, raw.handFaceRatio, raw.faceDetectionValid);
    const handNearMouth = decide(prev?.handNearMouth, raw.handMouthRatio, raw.faceDetectionValid);
    const handNearChin = decide(prev?.handNearChin, raw.handChinRatio, raw.faceDetectionValid);
    const headDown = decide(prev?.headDown, raw.headDownRatio, true);
    const leanForward = decide(prev?.leanForward, raw.leanForwardRatio, true);

    const parts: string[] = [];
    if (handNearMouth) parts.push('hand near mouth');
    else if (handNearChin) parts.push('hand near chin');
    else if (handNearFace) parts.push('hand near face');
    if (headDown) parts.push('head down');
    if (leanForward) parts.push('leaning forward');
    if (raw.bodyStill) parts.push('body still'); else parts.push('body moving');

    const result: BodyGestures = {
      handNearFace,
      handNearMouth,
      handNearChin,
      headDown,
      leanForward,
      bodyStill: raw.bodyStill,
      gesturesSummary: parts.join(', ') || 'no notable gestures',
      faceDetectionValid: raw.faceDetectionValid,
      handFaceRatio: raw.handFaceRatio,
      handMouthRatio: raw.handMouthRatio,
      handChinRatio: raw.handChinRatio,
      headDownRatio: raw.headDownRatio,
      leanForwardRatio: raw.leanForwardRatio,
      totalFrames: raw.totalFrames,
    };

    this.prevBodyGestures = result;
    console.log('🔧 Hysteresis applied to bodyGestures:', { raw: { handFaceRatio: raw.handFaceRatio, handMouthRatio: raw.handMouthRatio, headDownRatio: raw.headDownRatio }, final: { handNearFace: result.handNearFace, handNearMouth: result.handNearMouth, headDown: result.headDown } });
    return result;
  }

  async analyzeMilestone(data: MilestoneData): Promise<Diagnosis | null> {
    try {
      const formData = new FormData();
      formData.append('sessionId', data.sessionId);
      formData.append('context', 'healthcare_therapy');
      formData.append('isMilestone', data.isMilestone ? 'true' : 'false');
      if (data.windowSeconds !== undefined) {
        formData.append('windowSeconds', data.windowSeconds.toString());
      }
      if (data.snapshotCount !== undefined) {
        formData.append('snapshotCount', data.snapshotCount.toString());
      }
      
      if (data.chunkIndex !== undefined && data.chunkIndex > 0) {
        formData.append('chunkIndex', data.chunkIndex.toString());
      } else {
        console.error('❌ chunkIndex is missing or invalid:', data.chunkIndex);
        return null;
      }
      
      if (data.audioBase64) {
        formData.append('audio', data.audioBase64);
        formData.append('audioFormat', 'mp3');
      }
      
      // Dedup snapshots by timestamp (100ms tolerance for near-simultaneous detections)
      const dedupStates: typeof data.states = [];
      for (const s of data.states) {
        if (!dedupStates.some(d => Math.abs(d.timestamp - s.timestamp) < 100)) {
          dedupStates.push(s);
        }
      }

      const blendshapeSnapshots = dedupStates
        .filter(s => s.blendshapes)
        .map(s => ({ timestamp: s.timestamp, data: s.blendshapes }));
      
      if (blendshapeSnapshots.length > 0) {
        formData.append('blendshapeSnapshots', JSON.stringify(blendshapeSnapshots));
      }
      
      const faceLandmarksSnapshots = dedupStates
        .filter(s => s.faceLandmarks)
        .map(s => ({ timestamp: s.timestamp, landmarks: s.faceLandmarks }));
      
      if (faceLandmarksSnapshots.length > 0) {
        // Filter to essential landmarks only (13 points that add value beyond blendshapes)
        const essentialIndices = new Set([
          33, 133, 159, 145,  // Left eye (EAR calculation)
          362, 263, 374, 386, // Right eye (EAR calculation)
          172, 397,           // Jaw corners (tension detection)
          61, 291,            // Lip corners (asymmetry)
          17,                 // Lower lip center (compression)
        ]);
        const filtered = faceLandmarksSnapshots.map(s => ({
          ...s,
          landmarks: (s.landmarks || []).filter((lm: any) => essentialIndices.has(lm.i ?? -1)),
        }));
        const landmarkRanges = compressLandmarkRanges(filtered);
        formData.append('faceLandmarkRanges', JSON.stringify(landmarkRanges));
        formData.append('faceLandmarkSnapshotCount', faceLandmarksSnapshots.length.toString());
      }
      
      if (data.bodyPoseSnapshots && data.bodyPoseSnapshots.length > 0) {
        // Dedup by timestamp, remove worldLandmarks, filter out leg keypoints (25-32)
        const dedupPose: typeof data.bodyPoseSnapshots = [];
        for (const p of data.bodyPoseSnapshots) {
          if (!dedupPose!.some(d => Math.abs(d.timestamp - p.timestamp) < 100)) {
            dedupPose.push(p);
          }
        }
        const cleanedPose = dedupPose
          .map(p => ({
            timestamp: p.timestamp,
            keypoints: Array.isArray(p.keypoints)
              ? p.keypoints.filter((_: any, i: number) => i < 25)
              : p.keypoints,
            handLandmarks: (p as any).handLandmarks || undefined,
          }));
        // Range-compress: N snapshots → min/max/avg per keypoint
        const bodyPoseRanges = compressBodyPoseRanges(cleanedPose);
        formData.append('bodyPoseRanges', JSON.stringify(bodyPoseRanges));
        formData.append('bodyPoseSnapshotCount', cleanedPose.length.toString());

        // Compute semantic gestures from raw frames (before compression loses the signal)
        // Pass face detection validity flag to avoid spurious hand-to-face detections
        const safeFaceSnaps = faceLandmarksSnapshots.map(s => ({ timestamp: s.timestamp, landmarks: s.landmarks || [] }));
        const rawGestures = computeBodyGestures(cleanedPose, data.isFaceDetectionValid ?? true, safeFaceSnaps);
        if (rawGestures) {
          // Apply hysteresis/smoothing across calls to reduce flicker
          const finalGestures = this.applyHysteresis(rawGestures);
          formData.append('bodyGestures', JSON.stringify(finalGestures));
        }
      } else {
        formData.append('bodyPoseRanges', JSON.stringify({}));
      }
      
      if (data.heartRate && data.heartRate.confidence > 0) {
        formData.append('heartRate', JSON.stringify(data.heartRate));
      }
      
      if (data.baseline) {
        formData.append('baseline', JSON.stringify(data.baseline));
      }

      console.log('📤 Sending FormData fields:');
      for (const [key, value] of formData.entries()) {
        if (key === 'audio') {
          console.log(`  ${key}: [base64 audio data, length: ${(value as string).length}]`);
        } else {
          console.log(`  ${key}:`, typeof value === 'string' && value.length > 100 ? value.substring(0, 100) + '...' : value);
        }
      }

      const response = await fetch(`${this.apiUrl}/api/emotional-analysis/analyze-realtime`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
        },
        body: formData,
        signal: data.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API error response:', errorText);
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();
      const raw = result.data || result;
      const resultData = raw.output || raw;

      console.log('🔍 API response structure:', JSON.stringify({
        hasData: !!result.data,
        hasOutput: !!raw.output,
        transcript: resultData.transcript,
        transcriptType: typeof resultData.transcript,
        allKeys: Object.keys(resultData),
      }));

      const diagnosis: any = {
        context: resultData.context || '',
        insight: resultData.insight || '',
        recommendation: resultData.recommendation || '',
        riskLevel: resultData.riskLevel || 'low',
        emotionalPattern: resultData.emotionalPattern || '',
        reasoning: resultData.reasoning || '',
        transcript: resultData.transcript,
        timestamp: Date.now(),
      };
      
      if (resultData.emotion !== undefined) diagnosis.emotion = resultData.emotion;
      if (resultData.valence !== undefined) diagnosis.valence = resultData.valence;
      if (resultData.arousal !== undefined) diagnosis.arousal = resultData.arousal;
      if (resultData.confidence !== undefined) diagnosis.confidence = resultData.confidence;
      if (resultData.baseline) diagnosis.baseline = resultData.baseline;
      
      return diagnosis;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('🚫 API request aborted - session stopped');
        return null;
      }
      console.error('API call failed:', error);
      return null;
    }
  }

  async createSession(sessionId: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.apiUrl}/api/eie-sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.authToken}`,
        },
        body: JSON.stringify({ sessionId, startTime: Date.now() }),
      });
      return response.ok;
    } catch (error) {
      console.error('Session creation failed:', error);
      return false;
    }
  }

  async endSession(sessionId: string, summary: any): Promise<boolean> {
    try {
      const response = await fetch(`${this.apiUrl}/api/eie-sessions/${sessionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.authToken}`,
        },
        body: JSON.stringify({ endTime: Date.now(), summary }),
      });
      return response.ok;
    } catch (error) {
      console.error('Session end failed:', error);
      return false;
    }
  }
}
